import type { ExtractInjectionAPI, PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../aiPluginType';
import type { EditorPluginAIProvider } from '../../types/types';
import type { EditorPluginAIConfigItem } from '../../ui/prebuilt/config-items/config-items';

import { fireAiQuickPromptDisplayedEvent } from './analytics';
import { checkContentEligibility } from './content-eligibility';
import { hasTabConflict } from './context-conflict-detector';
import { createShowPromptCommand, getPluginState } from './plugin-factory';
import { checkShouldImproveWriting } from './should-improve-writing';
import type { StandDownManager } from './stand-down-manager';
import type { EligibleResult } from './types';

/**
 * Run all synchronous guard checks that determine whether the AI quick prompt
 * should be shown. This is extracted into a function so it can be called both
 * before and after the async `checkShouldImproveWriting` network request to
 * guard against race conditions (e.g. the user moves the cursor or the document
 * changes while the request is in-flight).
 *
 * @returns The {@link EligibleResult} when all guards pass, `undefined` otherwise.
 */
export function checkPromptGuards({
	api,
	getIntl,
	standDownManager,
	view,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	getIntl: PMPluginFactoryParams['getIntl'];
	standDownManager: StandDownManager;
	view: EditorView;
}): EligibleResult | undefined {
	// - Check if the prompt is already visible
	const pluginState = getPluginState(view.state);
	if (pluginState?.isPromptVisible && pluginState.eligibleRange) {
		return;
	}

	// - Check paste options toolbar
	const pasteOptionsToolbarPluginState = api?.pasteOptionsToolbarPlugin?.sharedState.currentState();
	if (pasteOptionsToolbarPluginState?.showToolbar) {
		return;
	}

	// - Check if AI is active (running or streaming or showing accept/reject review)
	// If there are multiple sessions, only checking isRunning is not enough.
	const aiState = api?.aiExperience?.sharedState.currentState();
	if (
		aiState?.aiDecorationExperiencePluginState?.isRunning ||
		aiState?.isStreaming ||
		aiState?.isShowingAcceptRejectScreen
	) {
		return;
	}

	// - Check stand-down
	if (standDownManager.shouldStandDown()) {
		return;
	}

	// - Check Tab conflict
	if (hasTabConflict(view.state.selection)) {
		return;
	}

	// - Check content eligibility
	const eligibleResult = checkContentEligibility({
		locale: getIntl().locale,
		selection: view.state.selection,
	});
	if (!eligibleResult) {
		return;
	}

	// - Check if the node was already triggered via Tab
	if (standDownManager.wasTriggered(eligibleResult.nodeId)) {
		return;
	}

	// - Check if the eligible text has been shown before
	const trimmedText = eligibleResult.text.trim();
	if (pluginState?.previousShownTexts?.includes(trimmedText)) {
		return;
	}

	return eligibleResult;
}

export const onIdle = async ({
	api,
	configItem,
	getIntl,
	product,
	signal,
	standDownManager,
	view,
}: {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	configItem: EditorPluginAIConfigItem;
	getIntl: PMPluginFactoryParams['getIntl'];
	product: EditorPluginAIProvider['product'];
	/** Optional abort signal to cancel the in-flight network request early. */
	signal?: AbortSignal;
	standDownManager: StandDownManager;
	view: EditorView;
}): Promise<void> => {
	// Run all synchronous guards before the network request
	const eligibleResult = checkPromptGuards({ api, getIntl, standDownManager, view });
	if (!eligibleResult) {
		return;
	}

	// - Check if the content should be improved via API
	const shouldImprove = await checkShouldImproveWriting({
		locale: getIntl().locale,
		product,
		range: eligibleResult.range,
		signal,
		view,
	});
	if (!shouldImprove) {
		return;
	}

	// Re-run all synchronous guards after the network request to guard against
	// race conditions: the user may have moved the cursor, changed the document,
	// or a collab update may have arrived while the request was in-flight.
	const eligibleResultAfterAwait = checkPromptGuards({ api, getIntl, standDownManager, view });
	if (!eligibleResultAfterAwait) {
		return;
	}

	// If the content of the eligible range has changed during the network
	// request, the API response may no longer be relevant — early return.
	if (eligibleResult.text !== eligibleResultAfterAwait.text) {
		return;
	}

	const trimmedText = eligibleResultAfterAwait.text.trim();

	// - Show the prompt
	// Use the fresh eligible range from after the await (positions may have
	// shifted due to collab updates even when the content is identical).
	createShowPromptCommand({ range: eligibleResultAfterAwait.range, shownText: trimmedText })(
		view.state,
		view.dispatch,
	);

	// - Fire analytics: prompt displayed
	fireAiQuickPromptDisplayedEvent({ api, experienceName: configItem.key });
};
